import React, { useState, useEffect, useCallback } from "react";
import { requestCorrection, getCorrectionHistory, cancelCorrection } from "../../../services/attendanceApi";
import { useToast } from "./Toast";
import Icons from "./Icons";

/**
 * CorrectionForm — request a correction to a past attendance record.
 *
 * Employees pick the work date, the corrected check-in / check-out times,
 * and a reason. On submit we call attendanceApi.requestCorrection(), which
 * maps our camelCase payload to the backend's snake_case body:
 *   { work_date, requested_check_in, requested_check_out, reason }.
 *
 * requestCheckIn/requestCheckOut are sent as full ISO datetimes (IST, +05:30)
 * built from the selected work date + time, matching how the permission and
 * admin-edit flows send times to the backend.
 */
function CorrectionForm({ onSubmitted, refreshKey }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ workDate: "", requestedCheckIn: "", requestedCheckOut: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Defensive history: only shown if the backend route exists.
  const [history, setHistory] = useState(null); // null = hidden/unavailable
  const [cancelling, setCancelling] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Defensive: attempt to load correction history. If the route doesn't
  // exist yet (404) or errors, keep history null so the section stays hidden.
  const loadHistory = useCallback(async () => {
    try {
      const res = await getCorrectionHistory();
      const data = res && res.data ? res.data : res;
      const list = Array.isArray(data) ? data : (data.corrections || data.history || []);
      setHistory(list);
    } catch {
      setHistory(null); // route unavailable — hide section silently
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory, refreshKey]);

  // Combine a date ("2026-09-30") + time ("09:15") into an IST ISO string.
  const toISO = (date, time) => {
    if (!date || !time) return null;
    const t = time.length === 5 ? `${time}:00` : time; // HH:MM -> HH:MM:SS
    return `${date}T${t}+05:30`;
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.workDate) {
      setError("Please select the work date to correct.");
      return;
    }
    if (!form.requestedCheckIn && !form.requestedCheckOut) {
      setError("Please provide a corrected check-in and/or check-out time.");
      return;
    }
    if (!form.reason.trim()) {
      setError("Please enter a reason for the correction.");
      return;
    }
    try {
      setSubmitting(true);
      await requestCorrection({
        workDate: form.workDate,
        requestedCheckIn: toISO(form.workDate, form.requestedCheckIn),
        requestedCheckOut: toISO(form.workDate, form.requestedCheckOut),
        reason: form.reason.trim(),
      });
      toast.success("Correction request submitted for approval.");
      setForm({ workDate: "", requestedCheckIn: "", requestedCheckOut: "", reason: "" });
      loadHistory();
      onSubmitted && onSubmitted();
    } catch (err) {
      const m = err.message || "Could not submit correction request.";
      setError(m);
      toast.error(m);
    } finally {
      setSubmitting(false);
    }
  };

  // Cancel/withdraw a PENDING correction request.
  // Depends on backend route PUT /attendance/correction/cancel/{id} (parallel).
  const handleCancel = async (id) => {
    if (!id) return;
    try {
      setCancelling(id);
      await cancelCorrection(id);
      toast.success("Correction request cancelled.");
      loadHistory();
      onSubmitted && onSubmitted();
    } catch (err) {
      toast.error(err.message || "Could not cancel correction request.");
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div className="atl-card atl-correctionform" id="atl-correction-form">
      <h3><span className="atl-h3ic">{Icons.history}</span>Request Attendance Correction</h3>

      {error && <div className="atl-error">{error}</div>}

      <div className="atl-field">
        <label>Work Date</label>
        <input type="date" className="atl-input" value={form.workDate} onChange={(e) => set("workDate", e.target.value)} />
      </div>
      <div className="atl-row">
        <div className="atl-field">
          <label>Corrected Check In</label>
          <input type="time" className="atl-input" value={form.requestedCheckIn} onChange={(e) => set("requestedCheckIn", e.target.value)} />
        </div>
        <div className="atl-field">
          <label>Corrected Check Out</label>
          <input type="time" className="atl-input" value={form.requestedCheckOut} onChange={(e) => set("requestedCheckOut", e.target.value)} />
        </div>
      </div>
      <div className="atl-field">
        <label>Reason</label>
        <textarea className="atl-input" rows={2} value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder="Reason for correction (e.g. forgot to check out)" />
      </div>
      <button className="atl-btn atl-btn--primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? "Submitting..." : "Submit Correction Request"}
      </button>

      {/* Recent correction requests — wired defensively; only rendered
          when GET /attendance/correction/history exists and returns data. */}
      {history && history.length > 0 && (
        <div className="atl-corr-history">
          <h4 className="atl-subhead">Recent Requests</h4>
          <div className="atl-table-wrap">
            <table className="atl-table">
              <thead>
                <tr><th>Work Date</th><th>Check In</th><th>Check Out</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {history.map((h, i) => {
                  const status = (h.status || "").toUpperCase();
                  const id = h.id || h.correction_id;
                  return (
                    <tr key={h.id || h.correction_id || i}>
                      <td data-label="Work Date">{fmtDate(h.work_date || h.workDate)}</td>
                      <td data-label="Check In">{fmtTime(h.requested_check_in || h.requestedCheckIn)}</td>
                      <td data-label="Check Out">{fmtTime(h.requested_check_out || h.requestedCheckOut)}</td>
                      <td data-label="Status"><span className={statusClass(status)}>{status || "—"}</span></td>
                      <td data-label="">
                        {status === "PENDING" && id && (
                          <button className="atl-link-btn" onClick={() => handleCancel(id)} disabled={cancelling === id}>
                            {cancelling === id ? "..." : "Cancel"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function statusClass(s) {
  switch ((s || "").toUpperCase()) {
    case "APPROVED": return "atl-pill atl-pill--present";
    case "PENDING": return "atl-pill atl-pill--late";
    case "REJECTED": return "atl-pill atl-pill--absent";
    // Cancelled requests remain in history (not deleted) with a muted badge.
    case "CANCELLED": return "atl-pill atl-pill--muted";
    default: return "atl-pill";
  }
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short" });
}

function fmtTime(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" });
}

export default CorrectionForm;
